import { useGoogleAnalytics } from "client/hooks/utils/useGoogleAnalytics";
import { useKeyboard } from "client/hooks/utils/useKeyboard";
import { useOnOutsideClick } from "client/hooks/utils/useOnOutsideClick";
import { TextBox, TextBoxStates } from "client/store/features/memeGenerator/types";
import { placeCaretAtEnd } from "client/utils/placeCaretAtEnd";
import { FormEvent, useRef, useEffect, useCallback, MouseEvent, PointerEvent } from "react";
import { deleteTextBox, setActiveTextBoxId, setSettings } from "store/features/memeGenerator/actions";
import { adjustFontSize } from "store/features/memeGenerator/utils/adjustFontSize";
import { checkIfTextBoxIsEmpty } from "store/features/memeGenerator/utils/checkIfTextBoxIsEmpty";
import { isTextBoxDragging } from "store/features/memeGenerator/utils/isTextBoxDragging";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { useTextBoxDrag } from "./useTextBoxDrag";

export const useTextBox = (textBox?: TextBox) => {
    const dispatch = useAppDispatch();
    const textBoxId = textBox?.id;
    const parentContainerRef = useRef<HTMLDivElement>(null);
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const activeTextBoxId = useAppSelector((state) => state.memeGenerator.meme?.activeTextBoxId);
    useOnOutsideClick(
        parentContainerRef,
        activeTextBoxId === textBoxId,
        handleOutsideClick,
        ["mg-controlbar", "textbox-ignore", "mobile-color-picker-modal"],
        { delay: 100 },
    );
    useKeyboard("Escape", activeTextBoxId === textBoxId, handleOutsideClick);
    const { gtag: onInputGtag } = useGoogleAnalytics(true);

    function onEnter() {
        if (!textBoxId) return;
        dispatch(
            setSettings({
                id: textBoxId,
                showBorder: true,
            }),
        );
    }

    function onLeave() {
        const textBox = store.getState().memeGenerator.meme?.textBoxes.find((_t) => _t.id === textBoxId);
        if (!textBox || textBox.state !== TextBoxStates.BLUR) return;
        dispatch(
            setSettings({
                id: textBox.id,
                showBorder: false,
            }),
        );
    }

    const { dragEventHandlers } = useTextBoxDrag(textBoxId, { onEnter, onLeave });

    function makeActive() {
        if (!textBox) return;
        const activeTextBoxId = store.getState().memeGenerator.meme?.activeTextBoxId;
        if (textBox.id === activeTextBoxId) return;
        dispatch(setActiveTextBoxId(textBox.id));
    }

    function setTextBoxState(state: TextBoxStates) {
        if (!textBoxId) return;
        dispatch(
            setSettings({
                id: textBoxId,
                state,
            }),
        );
    }

    function handleOutsideClick() {
        const textBox = store.getState().memeGenerator.meme?.textBoxes.find((_t) => _t.id === textBoxId);
        if (!textBox || isTextBoxDragging(textBox)) return;
        const currentActiveTextBoxId = store.getState().memeGenerator.meme?.activeTextBoxId;

        if (currentActiveTextBoxId === textBox.id) {
            dispatch(setActiveTextBoxId(undefined));
            setTextBoxState(TextBoxStates.BLUR);
        }
        if (textBox && checkIfTextBoxIsEmpty(textBox.innerHTML)) {
            dispatch(deleteTextBox(textBox.id));
        }

        dispatch(
            setSettings({
                id: textBox.id,
                showBorder: false,
            }),
        );
    }

    function onBlurOverlayClick(e: MouseEvent) {
        e.stopPropagation();
        setTextBoxState(TextBoxStates.PRE_INSERT);
        makeActive();
    }

    function onFocus() {
        setTextBoxState(TextBoxStates.INSERT);
        makeActive();
    }

    function onContentEditableClick(e: MouseEvent) {
        e.stopPropagation();
    }

    function onParentClick() {
        makeActive();
        contentEditableRef.current?.focus();
        contentEditableRef.current && placeCaretAtEnd(contentEditableRef.current);
    }

    const updateContentEditableDivFont = useCallback(() => {
        const element = contentEditableRef.current;
        if (!element || !textBox) return;
        [...element.children].map((c) => {
            c.removeAttribute("style");
            // @ts-ignore
            c.style.fontFamily = textBox.fontFamily;
            // @ts-ignore
            c.style.background = "none";
        });
        if (textBox.innerHTML !== element.innerHTML) {
            dispatch(
                setSettings({
                    id: textBox.id,
                    innerHTML: element.innerHTML,
                }),
            );
        }
    }, [textBox?.fontFamily]);

    const handleAdjustFontSize = useCallback(() => {
        if (!textBox) return;
        const contentEditableDiv = contentEditableRef.current;
        const container = parentContainerRef.current;
        if (!contentEditableDiv || !container) return;
        adjustFontSize(contentEditableDiv, container, textBox.fontSize);
        const fontSize = parseInt(getComputedStyle(contentEditableDiv).fontSize);
        if (!isNaN(fontSize)) {
            dispatch(
                setSettings({
                    id: textBox.id,
                    fontSize,
                }),
            );
        }
    }, [dispatch, textBox?.fontSize, textBox?.id]);

    function handleInput(e: FormEvent) {
        if (!textBox) return;
        onInputGtag("event", "meme_add_text", {
            page: "meme generator",
        });
        const target = e.target as HTMLElement;
        updateContentEditableDivFont();
        dispatch(
            setSettings({
                id: textBox.id,
                value: target.innerText ?? "",
                innerHTML: target.innerHTML,
            }),
        );
        handleAdjustFontSize();
    }

    function onContentEditablePointerDown(e: PointerEvent) {
        const textBox = store.getState().memeGenerator.meme?.textBoxes.find((_t) => _t.id === textBoxId);
        textBox?.state === TextBoxStates.INSERT && e.stopPropagation();
    }

    useEffect(updateContentEditableDivFont, [textBox?.fontFamily, updateContentEditableDivFont]);
    useEffect(handleAdjustFontSize, [textBox?.spacing, textBox?.fontFamily, handleAdjustFontSize]);
    useEffect(() => {
        const contentEditableDiv = contentEditableRef.current;
        const container = parentContainerRef.current;
        if (!contentEditableDiv || !textBox || !container) return;
        contentEditableDiv.innerHTML = textBox.value;
        dispatch(
            setSettings({
                id: textBox.id,
                innerHTML: contentEditableDiv.innerHTML,
            }),
        );
        adjustFontSize(contentEditableDiv, container, textBox.fontSize);
        const fontSize = parseInt(getComputedStyle(contentEditableDiv).fontSize);
        if (!isNaN(fontSize)) {
            dispatch(
                setSettings({
                    id: textBox.id,
                    fontSize,
                }),
            );
        }
    }, [contentEditableRef, parentContainerRef]);

    return {
        handleOutsideClick,
        dragEventHandlers,
        onBlurOverlayClick,
        onFocus,
        parentContainerRef,
        contentEditableRef,
        onContentEditableClick,
        onParentClick,
        handleInput,
        onContentEditablePointerDown,
    };
};
